import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { createStructuredNote, parseNotesBlob } from '../../utils/noteUtils';
import NoteCard from '../notes/NoteCard';
import config from '../../config';

const EmbeddedNotesSection = ({ 
  organizationId, 
  initialNotes = '',
  onNotesUpdate,
  title = "Notes",
  compact = false
}) => {
  const { user, getToken } = useAuth();
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);

  // Load initial notes when component mounts or organizationId changes
  useEffect(() => {
    if (organizationId) {
      // `initialNotes` is the raw `organization.notes` column - a blob of
      // concatenated notes. Split it into individually-attributed entries so
      // they render as separate cards instead of one jumbled paragraph.
      const initialNotesArray = parseNotesBlob(initialNotes, { idPrefix: 'db_note', noteType: 'general' });

      // Load localStorage notes
      const storageKey = `notes_organization_${organizationId}`;
      const storageNotes = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      // Merge and sort notes
      const allNotes = [...storageNotes, ...initialNotesArray];
      const uniqueNotes = allNotes.reduce((acc, note) => {
        const key = `${note.content}_${note.createdAt}`;
        if (!acc.some(n => `${n.content}_${n.createdAt}` === key)) {
          acc.push(note);
        }
        return acc;
      }, []);
      
      uniqueNotes.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setNotes(uniqueNotes);
    }
  }, [organizationId, initialNotes]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setLoading(true);

    const userName = user?.name || user?.email || 'Unknown User';
    // Use the same "(date) User from Source: content" structured format used
    // everywhere else so this note parses and displays consistently the next
    // time it's loaded (via parseNotesBlob).
    const raw = createStructuredNote(newNote.trim(), userName, 'organization', 'general');

    const noteData = {
      content: newNote.trim(),
      createdBy: userName,
      createdAt: new Date().toISOString(),
      noteType: 'general',
      isNew: true,
      raw
    };

    try {
      // Try to save to backend first if organizationId exists (edit mode)
      if (organizationId) {
        const token = await getToken();
        const response = await fetch(`${config.apiUrl}/api/organizations/${organizationId}/notes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            content: noteData.content,
            noteType: noteData.noteType
          })
        });

        if (response.ok) {
          const savedNote = await response.json();
          const newNoteWithId = {
            ...noteData,
            id: savedNote.id || Date.now().toString(),
            isNew: false
          };
          
          const updatedNotes = [newNoteWithId, ...notes];
          setNotes(updatedNotes);
          setNewNote('');
          
          // Clear localStorage note since it's now saved to backend
          const storageKey = `notes_organization_${organizationId}`;
          const storageNotes = JSON.parse(localStorage.getItem(storageKey) || '[]');
          const filteredStorageNotes = storageNotes.filter(n => n.content !== noteData.content);
          localStorage.setItem(storageKey, JSON.stringify(filteredStorageNotes));
          
          if (onNotesUpdate) {
            onNotesUpdate(updatedNotes, newNoteWithId, { localOnly: false });
          }
          setLoading(false);
          return;
        }
      }
      
      // Fallback to localStorage (for new organizations or backend failure)
      const localNote = {
        ...noteData,
        id: `local_${Date.now()}`
      };
      
      const updatedNotes = [localNote, ...notes];
      setNotes(updatedNotes);
      setNewNote('');
      
      // Save to localStorage
      if (organizationId) {
        const storageKey = `notes_organization_${organizationId}`;
        const existingNotes = JSON.parse(localStorage.getItem(storageKey) || '[]');
        existingNotes.unshift(localNote);
        localStorage.setItem(storageKey, JSON.stringify(existingNotes));
      }
      
      if (onNotesUpdate) {
        onNotesUpdate(updatedNotes, localNote, { localOnly: true });
      }
    } catch (error) {
      
      // Fallback to localStorage
      const localNote = {
        ...noteData,
        id: `local_${Date.now()}`
      };
      
      const updatedNotes = [localNote, ...notes];
      setNotes(updatedNotes);
      setNewNote('');
      
      if (organizationId) {
        const storageKey = `notes_organization_${organizationId}`;
        const existingNotes = JSON.parse(localStorage.getItem(storageKey) || '[]');
        existingNotes.unshift(localNote);
        localStorage.setItem(storageKey, JSON.stringify(existingNotes));
      }
      
      if (onNotesUpdate) {
        onNotesUpdate(updatedNotes, localNote, { localOnly: true });
      }
    }
    
    setLoading(false);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      handleAddNote();
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      
      {/* Add New Note Section */}
      <Paper sx={{ p: compact ? 1.5 : 2, bgcolor: 'grey.50', mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon fontSize="small" />
          Add New Note
        </Typography>
        
        {/* Note Type Selector - Only General for organizations */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" gutterBottom display="block">
            Note Type:
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip
              label="General"
              variant="filled"
              color="primary"
              size="small"
            />
          </Stack>
        </Box>
        
        <TextField
          fullWidth
          multiline
          rows={compact ? 2 : 3}
          placeholder="Enter your note here... (Ctrl+Enter to save quickly)"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddNote}
            disabled={!newNote.trim() || loading}
          >
            {loading ? 'Adding...' : 'Add Note'}
          </Button>
        </Box>
      </Paper>

      {/* Notes History */}
      <Box>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimeIcon fontSize="small" />
          Notes History ({notes.length})
        </Typography>
        
        {notes.length === 0 ? (
          <Paper sx={{ p: compact ? 2 : 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              No notes available. Add the first note above.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={compact ? 1 : 2} sx={{ maxHeight: compact ? 200 : 300, overflow: 'auto' }}>
            {notes.map((note, index) => (
              <NoteCard
                key={note.id || index}
                note={{ ...note, isLocal: note.id?.toString().startsWith('local_') }}
                color="primary"
                bgColor="blue.50"
                borderColor="blue.200"
              />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default EmbeddedNotesSection;
